import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { debounce } from 'lodash';
import { useAppDispatch } from 'slices/store';
import { fetchMoreUsers, fetchUsersSuccess } from 'slices/user.slice';
import type { TFindManyTEUsersBody, TTEUser } from '@timeedit/types';
import {
  type GetMore,
  TEInput,
  useClientHeight,
  useInfiniteScroll,
} from '@timeedit/ui-components';
import { Select, type SelectProps } from '@timeedit/ui-components/antd';

export type TExternalUserSelectComponentProps = PropsWithChildren<{
  filterOption: SelectProps['filterOption'];
  onChange: (value: string) => void;
  mode: SelectProps['mode'];
  label: string;
  tooltip?: string;
  value: any;
}>;

const MAX_RESULTS = 50;
const SCROLL_CONTAINER_CLASS_NAME = '.rc-virtual-list-holder';
const SCROLL_ITEM_CLASS_NAME = '.ant-select-item-option';

const filters = {};

const ExternalUserSelectComponent = ({
  children,
  filterOption,
  label,
  tooltip,
  mode,
  onChange,
  value,
}: TExternalUserSelectComponentProps) => {
  const dispatch = useAppDispatch();
  const [searchString, setSearchString] = useState('');

  const handleSearch = useMemo(
    () => debounce(setSearchString, 500, { leading: false, trailing: true }),
    []
  );

  const filterSettings = useMemo(
    () => ({
      filters,
      freeTextSearch: searchString,
    }),
    [searchString]
  );

  const findManyFilterBody = useMemo<TFindManyTEUsersBody>(() => {
    return {
      filter: filterSettings.filters,
      limit: MAX_RESULTS,
      page: 1,
      searchString: filterSettings.freeTextSearch,
    };
  }, [filterSettings]);

  const getMore = useCallback<GetMore<TTEUser>>(
    async (options) => {
      const { results, page, totalPages, totalResults } = await dispatch(
        fetchMoreUsers({
          ...findManyFilterBody,
          ...options,
        })
      ).unwrap();

      return {
        data: results,
        page,
        count: results.length,
        total: totalResults,
        hasMore: results.length === MAX_RESULTS && page < totalPages,
      };
    },
    [dispatch, findManyFilterBody]
  );

  const containerHeight = useClientHeight(SCROLL_CONTAINER_CLASS_NAME);
  const itemHeight = useClientHeight(SCROLL_ITEM_CLASS_NAME);

  const { onScroll, reset } = useInfiniteScroll({
    className: SCROLL_CONTAINER_CLASS_NAME,
    containerHeight,
    itemHeight,
    getMore,
    limit: MAX_RESULTS,
    virtual: true,
  });

  useEffect(() => {
    dispatch(fetchUsersSuccess([]));
    reset();
  }, [dispatch, getMore, reset]);

  return (
    <TEInput.LabelWrapper label={label} tooltip={tooltip}>
      <Select
        size="small"
        showSearch
        mode={mode}
        filterOption={filterOption}
        value={value}
        onChange={onChange}
        onSearch={handleSearch}
        onPopupScroll={onScroll}
        allowClear
      >
        {children}
      </Select>
    </TEInput.LabelWrapper>
  );
};

export default ExternalUserSelectComponent;
